/**
 * Nomenclatorul de furnizori.
 *
 * Există pentru o propoziție a lui Alexandru (17.09.2026): „la furnizori interni sunt cazuri în
 * care caut furnizorul și nu-l găsesc, pentru că nu există". Până acum singura cale prin care un
 * furnizor ajungea în ERP era ca cineva din contabilitate să-l creeze acolo cu mâna — prin CRM nu
 * se putea, fiindcă partenerii noi intrau în ERP doar ca efect secundar al primei comenzi, iar
 * furnizorii n-au comenzi.
 *
 * Două feluri de furnizori, două formulare, fiindcă sunt două lumi diferite:
 *
 *  - firmă din România → se caută după CUI, iar ANAF completează restul. Verificăm și în ERP,
 *    ca să nu creăm ceva ce există deja acolo;
 *  - furnizor extern → Google Ireland, Meta, TikTok. ANAF nu-i știe și n-au CUI românesc, deci
 *    se scriu numele și țara.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Search, Trash2, RefreshCw } from 'lucide-react'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { useAuthStore } from '../../stores/auth'
import { AddClientModal } from '../../components/orders/AddClientModal'

interface RandFurnizor {
  id: number
  nume: string
  cif: string | null
  tara: string
  oras: string | null
  eaSyncStatus: string
  eaSyncError: string | null
  facturi: number
  creatDe: string | null
  createdAt: string
}

interface RezultatCautare {
  id: number | null
  nume: string
  cif: string | null
  tara: string
  adresa: string | null
  oras: string | null
  judet: string | null
  regCom: string | null
  platitorTva: boolean | null
  sursa: 'crm' | 'ea' | 'istoric' | 'anaf'
  inEa: boolean
  facturiVechi?: number
  inactiv?: boolean
}

interface FurnizorCreat {
  id: number
  nume: string
  ea: 'trimis' | 'exista deja' | 'neconfigurat' | 'esuat'
  eaMesaj?: string
}

function eroare(e: unknown): string {
  const m = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message
  return m ?? 'A apărut o eroare.'
}

/** Ce s-a întâmplat cu trimiterea în ERP. Nu se ascunde: dacă n-a plecat, trebuie știut acum. */
function mesajErp(f: FurnizorCreat): { fel: 'ok' | 'atentie'; text: string } {
  switch (f.ea) {
    case 'trimis':
      return { fel: 'ok', text: `„${f.nume}” a fost creat și trimis în ExpertAccounts.` }
    case 'exista deja':
      return { fel: 'ok', text: `„${f.nume}” a fost adăugat. În ExpertAccounts exista deja.` }
    case 'neconfigurat':
      return {
        fel: 'atentie',
        text: `„${f.nume}” a fost creat în CRM, dar nu a ajuns în ExpertAccounts: legătura pentru parteneri noi nu e configurată (Setări › Integrări › ExpertAccounts).`,
      }
    default:
      return {
        fel: 'atentie',
        text: `„${f.nume}” a fost creat în CRM, dar ExpertAccounts l-a refuzat: ${f.eaMesaj ?? 'fără detalii'}`,
      }
  }
}

const STARE_ERP: Record<string, { text: string; clasa: string }> = {
  synced: { text: 'în EA', clasa: 'bg-emerald-100 text-emerald-700' },
  pending: { text: 'n-a ajuns în EA', clasa: 'bg-amber-100 text-amber-700' },
  error: { text: 'refuzat de EA', clasa: 'bg-red-100 text-red-700' },
}

export function FurnizoriPage() {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('marketing.suppliers:write')

  const [cauta, setCauta] = useState('')
  const [adauga, setAdauga] = useState<null | 'romania' | 'extern'>(null)

  const lista = useQuery<{ randuri: RandFurnizor[] }>({
    queryKey: ['furnizori', cauta],
    queryFn: async () =>
      (await api.get('/furnizori', { params: { cauta: cauta || undefined } })).data,
  })

  const sterge = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/furnizori/${id}`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['furnizori'] })
      toast.success('Scos din nomenclator.')
    },
    onError: (e) => toast.error(eroare(e)),
  })

  const retrimite = useMutation({
    mutationFn: async (id: number) =>
      (await api.post<{ furnizor: FurnizorCreat }>(`/furnizori/${id}/retrimite`)).data.furnizor,
    onSuccess: (f) => {
      const m = mesajErp(f)
      if (m.fel === 'ok') toast.success(m.text)
      else toast.warning(m.text, { duration: 12_000 })
      void qc.invalidateQueries({ queryKey: ['furnizori'] })
    },
    onError: (e) => toast.error(eroare(e)),
  })

  const randuri = lista.data?.randuri ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Furnizori</h1>
          <p className="text-sm text-b4l-text-muted">
            Aceeași listă pentru facturile din SPV și pentru cele externe. Furnizorii creați aici
            ajung și în ExpertAccounts.
          </p>
        </div>
        {poateScrie && (
          <div className="flex gap-2">
            <Button onClick={() => setAdauga('romania')}>
              <Plus className="mr-1 h-4 w-4" />
              Firmă din România
            </Button>
            <Button variant="secondary" onClick={() => setAdauga('extern')}>
              <Plus className="mr-1 h-4 w-4" />
              Furnizor extern
            </Button>
          </div>
        )}
      </div>

      {adauga === 'romania' && (
        <AdaugaDinRomania
          onGata={() => {
            setAdauga(null)
            void qc.invalidateQueries({ queryKey: ['furnizori'] })
          }}
          onRenunta={() => setAdauga(null)}
        />
      )}
      {adauga === 'extern' && (
        <AdaugaExtern
          onGata={() => {
            setAdauga(null)
            void qc.invalidateQueries({ queryKey: ['furnizori'] })
          }}
          onRenunta={() => setAdauga(null)}
        />
      )}

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Search className="h-4 w-4 text-b4l-text-muted" />
          <input
            value={cauta}
            onChange={(e) => setCauta(e.target.value)}
            placeholder="caută după nume sau cod fiscal…"
            className="w-full max-w-sm rounded border border-b4l-border px-2 py-1 text-sm"
          />
          <span className="text-xs text-b4l-text-muted">
            {randuri.length} {randuri.length === 1 ? 'furnizor' : 'furnizori'}
          </span>
        </div>

        {lista.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {!lista.isLoading && randuri.length === 0 && (
          <p className="text-sm text-b4l-text-muted">
            {cauta ? 'Niciun furnizor cu numele ăsta.' : 'Nomenclatorul e gol încă.'}
          </p>
        )}

        {randuri.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs text-b4l-text-muted">
                  <th className="p-1.5">Denumire</th>
                  <th className="p-1.5">Cod fiscal</th>
                  <th className="p-1.5">Țara</th>
                  <th className="p-1.5">EA</th>
                  <th className="p-1.5 text-right">Facturi</th>
                  <th className="p-1.5">Adăugat de</th>
                  <th className="p-1.5" />
                </tr>
              </thead>
              <tbody>
                {randuri.map((f) => {
                  const st = STARE_ERP[f.eaSyncStatus] ?? STARE_ERP.pending
                  return (
                    <tr key={f.id} className="border-b border-b4l-border">
                      <td className="p-1.5 font-medium">{f.nume}</td>
                      <td className="p-1.5 tabular-nums">{f.cif ?? '—'}</td>
                      <td className="p-1.5">
                        {f.tara}
                        {f.oras && <span className="text-b4l-text-muted"> · {f.oras}</span>}
                      </td>
                      <td className="p-1.5">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] ${st.clasa}`}>
                          {st.text}
                        </span>
                        {f.eaSyncError && (
                          <div
                            className="mt-0.5 max-w-xs truncate text-[11px] text-b4l-red"
                            title={f.eaSyncError}
                          >
                            {f.eaSyncError}
                          </div>
                        )}
                      </td>
                      <td className="p-1.5 text-right tabular-nums">{f.facturi || '—'}</td>
                      <td className="p-1.5 text-xs text-b4l-text-muted">
                        {f.creatDe ?? '—'}
                        {` · ${new Date(f.createdAt).toLocaleDateString('ro-RO')}`}
                      </td>
                      <td className="p-1.5 text-right">
                        {poateScrie && (
                          <div className="flex justify-end gap-2">
                            {f.eaSyncStatus !== 'synced' && (
                              <button
                                type="button"
                                onClick={() => retrimite.mutate(f.id)}
                                disabled={retrimite.isPending}
                                className="text-xs text-b4l-blue hover:underline disabled:opacity-50"
                                title="Încearcă din nou trimiterea în ExpertAccounts"
                              >
                                <RefreshCw className="inline h-3 w-3" /> EA
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm(`Scoți „${f.nume}" din nomenclator?`)) {
                                  sterge.mutate(f.id)
                                }
                              }}
                              className="text-xs text-b4l-red hover:underline"
                              title={
                                f.facturi > 0
                                  ? 'E pe facturi — nu se poate scoate'
                                  : 'Scoate din nomenclator'
                              }
                            >
                              <Trash2 className="inline h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * Firmă din România: se caută după CUI, iar ANAF completează restul.
 *
 * Căutarea trece întâi prin nomenclator și prin ERP. Dacă firma e deja într-unul din ele, se
 * arată asta în loc să se propună creare — exact întrebarea „există deja?" la care trebuia
 * răspuns înainte să trimitem ceva în ERP.
 */
function AdaugaDinRomania({ onGata, onRenunta }: { onGata: () => void; onRenunta: () => void }) {
  const [cui, setCui] = useState('')
  const [rezultate, setRezultate] = useState<RezultatCautare[] | null>(null)

  const cautare = useMutation({
    mutationFn: async () =>
      (
        await api.get<{ rezultate: RezultatCautare[] }>('/furnizori/cauta', {
          params: { q: cui.trim(), anaf: '1' },
        })
      ).data.rezultate,
    onSuccess: setRezultate,
    onError: (e) => toast.error(eroare(e)),
  })

  const creeaza = useMutation({
    mutationFn: async (r: RezultatCautare) =>
      (
        await api.post<{ furnizor: FurnizorCreat }>('/furnizori', {
          nume: r.nume,
          cif: r.cif,
          tara: r.tara,
          regCom: r.regCom,
          adresa: r.adresa,
          oras: r.oras,
          judet: r.judet,
          platitorTva: r.platitorTva,
          sursa: r.sursa,
          existaInEa: r.inEa,
        })
      ).data.furnizor,
    onSuccess: (f) => {
      const m = mesajErp(f)
      if (m.fel === 'ok') toast.success(m.text)
      else toast.warning(m.text, { duration: 12_000 })
      onGata()
    },
    onError: (e) => toast.error(eroare(e)),
  })

  return (
    <Card title="Furnizor nou — firmă din România">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
          Cod fiscal (CUI)
          <input
            value={cui}
            onChange={(e) => setCui(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && cui.trim().length >= 2) cautare.mutate()
            }}
            placeholder="37126475"
            autoFocus
            className="w-48 rounded border border-b4l-border px-2 py-1 text-sm"
          />
        </label>
        <Button
          onClick={() => cautare.mutate()}
          disabled={cui.trim().length < 2 || cautare.isPending}
        >
          {cautare.isPending ? 'Se caută…' : 'Caută'}
        </Button>
        <button
          type="button"
          onClick={onRenunta}
          className="text-xs text-b4l-text-muted hover:underline"
        >
          Renunță
        </button>
      </div>

      {rezultate && rezultate.length === 0 && (
        <p className="mt-3 text-sm text-b4l-text-muted">
          Nimic găsit — nici în nomenclator, nici în ExpertAccounts, nici la ANAF. Verifică codul.
        </p>
      )}

      {rezultate && rezultate.length > 0 && (
        <div className="mt-3 space-y-2">
          {rezultate.map((r) => (
            <div
              key={`${r.sursa}-${r.id ?? r.nume}`}
              className="rounded border border-b4l-border p-2"
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium">{r.nume}</span>
                <span className="text-xs text-b4l-text-muted">{r.cif ?? 'fără cod'}</span>
                {r.inactiv && (
                  <span className="rounded bg-red-100 px-1.5 text-[10px] text-red-700">
                    inactivă la ANAF
                  </span>
                )}
              </div>
              {(r.adresa || r.regCom) && (
                <div className="mt-0.5 text-xs text-b4l-text-muted">
                  {r.regCom && <span className="mr-2">{r.regCom}</span>}
                  {r.adresa}
                </div>
              )}
              <div className="mt-1.5">
                {r.id != null ? (
                  <span className="text-xs text-emerald-700">
                    E deja în nomenclator — nu mai e nimic de făcut.
                  </span>
                ) : r.inEa ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-b4l-text-muted">
                      Există în ExpertAccounts, dar nu și în CRM.
                    </span>
                    <Button
                      className="!px-2 !py-0.5 !text-xs"
                      onClick={() => creeaza.mutate(r)}
                      disabled={creeaza.isPending}
                    >
                      Adaugă în CRM
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="!px-2 !py-0.5 !text-xs"
                    onClick={() => creeaza.mutate(r)}
                    disabled={creeaza.isPending}
                  >
                    {creeaza.isPending
                      ? 'Se creează…'
                      : 'Creează în CRM și trimite în ExpertAccounts'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

/**
 * Furnizor extern. Fără ANAF și fără CUI, fiindcă n-au niciuna: Google Ireland are IE6388047V,
 * TikTok UK un cod britanic, iar unii nu trec niciun cod pe factură.
 */
/** Constantă: fereastra se precompletează la fiecare schimbare a obiectului. */
const FARA_TARA = { tara: '' }

function AdaugaExtern({ onGata, onRenunta }: { onGata: () => void; onRenunta: () => void }) {
  // Fereastra comună de parteneri, rolul de furnizor: aceleași reguli, pleacă în EA la salvare.
  // Codul de TVA rămâne opțional la furnizorii străini; țara se alege, nu se presupune.
  return (
    <AddClientModal
      rol="furnizor"
      open
      onClose={onRenunta}
      precompletare={FARA_TARA}
      onCreated={() => onGata()}
    />
  )
}
